import * as React from 'react';
import { IconButton, classNamesFunction, BaseButton, Text, useKeytipRef } from '@fluentui/react';
import {
    ITextFieldWithActionProps,
    ITextFieldWithActionStyleProps,
    ITextFieldWithActionStyles,
    ITextFieldWithAction,
} from './TextFieldWithAction.types';
import { assert } from './assertUtils';
import { useMergedRefs } from '@fluentui/react-hooks';

const getClassNames = classNamesFunction<
    ITextFieldWithActionStyleProps,
    ITextFieldWithActionStyles
>();

function preventDefaultCallback(ev: React.MouseEvent<HTMLDivElement>): void {
    ev.preventDefault();
}

function setTextInputValue(textInput: React.RefObject<HTMLInputElement>, value?: string): void {
    if (textInput.current) {
        textInput.current.value = value ?? '';
    }
}

function onKeytipExecute(executeTarget: HTMLElement | null): void {
    executeTarget?.focus();
}

function defaultOnRenderPrefix(prefix?: string): JSX.Element | undefined {
    if (prefix === undefined) {
        return undefined;
    }

    return <Text>{prefix}</Text>;
}

/**
 * The unstyled base control consumed by {@link TextFieldWithAction}
 * @param props {@link ITextFieldWithActionProps}
 */
export const TextFieldWithActionBase = React.forwardRef<HTMLDivElement>(
    (props: React.PropsWithChildren<ITextFieldWithActionProps>, ref): JSX.Element => {
        const {
            className,
            componentRef,
            iconProps = { iconName: 'ChevronDown-svg' },
            onButtonClick,
            styles,
            theme,
            onBlur,
            buttonChecked = false,
            onPendingValueChanged,
            onSubmit,
            onCancel,
            value,
            disabled,
            hideFocusVisual = false,
            prefix,
            onRenderPrefix = defaultOnRenderPrefix,
            keytipProps,
            ...allHtmlAttributes
        } = props;

        // Store props.value into this variable so that the cancelPendingChangeCallback
        //  doesn't need to depend on props.value
        const valueFromProp = React.useRef<string>();

        const textInput = React.useRef<HTMLInputElement>(null);

        React.useEffect(() => {
            setTextInputValue(textInput, value);
            valueFromProp.current = value;
        }, [value]);

        const [isActive, setIsActive] = React.useState(false);
        const [currentPrefix, setCurrentPrefix] = React.useState<JSX.Element>();

        const newPrefix = React.useMemo(() => onRenderPrefix(prefix), [onRenderPrefix, prefix]);

        // If we're setting a new prefix, update the current prefix.
        // Otherwise if we're removing the prefix, let the animation play out first
        if (newPrefix !== undefined && newPrefix !== currentPrefix) {
            setCurrentPrefix(newPrefix);
        }

        const onPrefixAnimationEndCallback = React.useCallback(() => {
            // If the animation was triggered because we're removing the prefix, when it's over
            // update our current prefix
            if (newPrefix === undefined) {
                setCurrentPrefix(undefined);
            }
        }, [newPrefix]);

        // Our prefix is exiting if there is currently one set, but our new prefix is empty
        const prefixExiting = currentPrefix !== undefined && newPrefix === undefined;

        assert(theme);
        const classNames = getClassNames(styles, {
            theme: theme,
            isActive: isActive || buttonChecked,
            className,
            disabled: disabled || false,
            hideFocusVisual,
            prefixExiting,
        });

        const iconButtonStyles = React.useMemo(
            () => classNames.subComponentStyles.buttonStyles(),
            [classNames.subComponentStyles]
        );

        const cancelPendingChangeCallback = React.useCallback((alsoSelectAllText) => {
            setTextInputValue(textInput, valueFromProp.current);
            if (alsoSelectAllText) {
                textInput.current?.select();
            }
        }, []);

        React.useImperativeHandle(
            componentRef,
            (): ITextFieldWithAction => ({
                focus: (): void => textInput.current?.focus(),
                blur: (): void => textInput.current?.blur(),
                cancelPendingChange: cancelPendingChangeCallback,
                setPendingText: (pendingText): void => setTextInputValue(textInput, pendingText),
            }),
            [cancelPendingChangeCallback]
        );

        const onFocusCallback = React.useCallback(() => {
            setIsActive(true);
            cancelPendingChangeCallback(true);
        }, [cancelPendingChangeCallback]);

        const onBlurCallback = React.useCallback(
            (ev: React.FocusEvent<HTMLInputElement>) => {
                setIsActive(false);
                onBlur?.(ev);
            },
            [onBlur]
        );

        const onChangeCallback = React.useCallback(
            (ev: React.ChangeEvent<HTMLInputElement>) => {
                onPendingValueChanged?.(ev.target.value);
                ev.stopPropagation();
            },
            [onPendingValueChanged]
        );

        const onKeyDownCallback = React.useCallback(
            (ev: React.KeyboardEvent<HTMLInputElement>) => {
                if (ev.key === 'Enter' || ev.key == 'Tab') {
                    onSubmit?.(ev, textInput.current?.value, valueFromProp.current);
                } else if (ev.key === 'Escape') {
                    onCancel && onCancel(ev);
                }
            },
            [onSubmit, onCancel]
        );

        const buttonOnClickCallback = React.useCallback(
            (
                event: React.MouseEvent<
                    | HTMLButtonElement
                    | HTMLDivElement
                    | HTMLAnchorElement
                    | BaseButton
                    | HTMLSpanElement,
                    MouseEvent
                >
            ) => {
                textInput.current?.focus();
                onButtonClick && onButtonClick(event);
            },
            [textInput, onButtonClick]
        );

        const onPointerDown = React.useCallback(
            (ev: React.PointerEvent<HTMLInputElement>) => {
                if (ev.pointerType === 'touch') {
                    textInput.current?.focus();

                    // for touch event, we want to open the dropdown only. We don't want to close it.
                    if (!buttonChecked) {
                        onButtonClick?.(ev);
                    }

                    ev.stopPropagation();
                    ev.preventDefault();
                }
            },
            [onButtonClick, buttonChecked]
        );

        if (keytipProps && !keytipProps.onExecute) {
            keytipProps.onExecute = onKeytipExecute;
        }

        const keytipRef = useKeytipRef<HTMLDivElement>({
            keytipProps,
            disabled,
        });
        assert(typeof keytipRef === 'function');

        // Merge 'ref' with 'keytipRef' so that the useKeytipRef() function will assign the
        // 'data-ktp-target' to the root <div> element.
        const rootRef = useMergedRefs(keytipRef, ref);

        return (
            <div className={classNames.root} ref={rootRef}>
                {currentPrefix && (
                    <div
                        className={classNames.prefix}
                        onAnimationEnd={onPrefixAnimationEndCallback}
                    >
                        {currentPrefix}
                    </div>
                )}
                <input
                    ref={textInput}
                    // Must set this 'data-ktp-execute-target' attribute so that the useKeytipRef() function will
                    // assign data-ktp* attributes to the <input> element. Otherwise, it will assign those
                    // to the <div> above.
                    data-ktp-execute-target={keytipProps ? '' : undefined}
                    type="text"
                    onFocus={onFocusCallback}
                    onBlur={onBlurCallback}
                    onPointerDown={onPointerDown}
                    autoComplete="off"
                    spellCheck={false}
                    translate="no"
                    data-lpignore={true}
                    className={classNames.textInput}
                    onChange={onChangeCallback}
                    onKeyDown={onKeyDownCallback}
                    readOnly={disabled}
                    {...allHtmlAttributes}
                />
                <IconButton
                    data-is-focusable={false}
                    ariaHidden={true}
                    tabIndex={-1}
                    styles={iconButtonStyles}
                    iconProps={iconProps}
                    onMouseDown={preventDefaultCallback}
                    onClick={buttonOnClickCallback}
                    checked={buttonChecked}
                    disabled={disabled}
                />
            </div>
        );
    }
);

TextFieldWithActionBase.displayName = 'TextFieldWithActionBase';
